import * as React from 'react';
import { FC, useMemo } from 'react';
import { Form, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { ChangeLog, GeneralSettingsData, Invoice, InvoiceDocumentType, InvoiceReduced, InvoiceState, PackSettings, RecurrenceFrequency } from '../../../models';
import { Translate, useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AppointmentEntity, ClientEntity, ClientPackEntity, Entity } from '../../../store/entities/state';
import { AutocompleteTextField, TextInputField, NumberInputField, DateField } from '../fields';
import ChangeLogInfo from '../../ChangeLogInfo';
import { getNowDateString, minutesToTime, parseTimeStringToMinutes } from '../../../shared/utils';
import { Delete } from '@material-ui/icons';
import ReceiptIcon from '@material-ui/icons/Receipt';
import Typography from '../../Typography';
import { hide } from '../../../shared/style';
import { InvoicesTable } from '../../../pages/Clients/ClientInvoicesTable';
import { useDispatch, useSelector } from 'react-redux';
import { updateChangingEntity } from '../../../store/entities/actions';
import moment from 'moment';
import { getTotalPayments, serviceName } from '../../../shared/appointmint/appointment';
import { PaymentsList } from '../fields/PaymentsList';
import { PaymentsButtons } from '../fields/PaymentsButtons';
import { formatEuros } from '../../../shared/string';
import { useExpensesAvailable, useGeneralSettings } from '../../../store/settings/hooks';
import ExtrasList from '../ExtrasList';
import AppButton from '../../AppButton';
import { usePackTemplates } from '../../../hooks/usePackTemplates';
import { useTaxData } from '../../../hooks/useTaxData';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { createClientSearchConfig } from '../../../shared/clientSearch';
import { getClientPackAsync } from '../../../ajax/requests';

export interface ClientPackFormStateProps {
    packSettings?: PackSettings,
    clientPack: ClientPackEntity,
    appointments?: Record<string, AppointmentEntity>,
    type: 'edit' | 'create',
    hasChanged?: boolean,
    readOnly?: boolean,
    canSeePrices: boolean,
    canSeeInvoices?: boolean,
    canCreateInvoices?: boolean,
    isAdministrator: boolean,
    loading?: boolean,
    changeLog?: ChangeLog[],
}

export interface ClientPackFormDispatchProps {
    onLoad?: () => void,
    onChange?: (value: ClientPackEntity) => void,
    onSubmit?: (value: ClientPackEntity) => void,
    onLoadAppointment?: (appointmintId: string) => void
    onOpenAppointment?: (appointmintId: string) => void
    onClose?: () => void,
    onDelete?: () => void,
}

const getAppointmentDesc = (packClientName: string, a: AppointmentEntity, settings: GeneralSettingsData) => {
    const duration = parseTimeStringToMinutes(a.end) - parseTimeStringToMinutes(a.start)
    return `${a.date} - ${a.professionalName} (${serviceName(a.type, settings)}) (${duration} min)`
        + (packClientName !== a.clientName ? ` (${a.clientName})` : '')
}


const subscriptionPriceDenomitator = (t: Translate<TK>, packSettings: PackSettings): string | undefined => {
    if (packSettings.subscriptionUnit === RecurrenceFrequency.Daily) {
        return (packSettings.subscriptionPeriod ?? 1) == 1 ? t(TK.day) : `${packSettings.subscriptionPeriod} ${t(TK.days)}`
    }
    if (packSettings.subscriptionUnit === RecurrenceFrequency.Weekly) {
        return (packSettings.subscriptionPeriod ?? 1) == 1 ? t(TK.week) : `${packSettings.subscriptionPeriod} ${t(TK.weeks)}`
    }
    if (packSettings.subscriptionUnit === RecurrenceFrequency.Monthly) {
        return (packSettings.subscriptionPeriod ?? 1) == 1 ? t(TK.month) : `${packSettings.subscriptionPeriod} ${t(TK.months)}`
    }
    return ''
}

export const calculateMissingToPayForSubscription = (
    totalPrice: number,
    packSettings: PackSettings,
    clientPack: ClientPackEntity
): number => {
    const payments = clientPack.payments ?? [];
    const totalPaid = payments.reduce((sum, p) => sum + (p.value || 0), 0);

    const since = clientPack.validityFrom || clientPack.creationDate;
    if (
        !since ||
        !totalPrice ||
        packSettings.subscriptionUnit == undefined
    ) return 0;

    const start = moment(since);
    const end = clientPack.validityTo !== undefined && moment(clientPack.validityTo).isBefore(moment())
        ? moment(clientPack.validityTo)
        : moment();

    let totalToPay = 0;
    const period = packSettings.subscriptionPeriod ?? 1;
    const price = totalPrice;

    if (packSettings.subscriptionUnit === RecurrenceFrequency.Daily) {
        const days = end.diff(start, 'days');
        totalToPay = (Math.floor(days / period) + 1) * price;
    } else if (packSettings.subscriptionUnit === RecurrenceFrequency.Weekly) {
        const weeks = end.diff(start, 'weeks');
        totalToPay = (Math.floor(weeks / period) + 1) * price;
    } else if (packSettings.subscriptionUnit === RecurrenceFrequency.Monthly) {
        const months = end.diff(start, 'months');
        totalToPay = Math.max(0, Math.floor(months / period) + 1) * price;
    } else {
        throw new Error(`Missing implementation for subscription unit ${packSettings.subscriptionUnit}`);
    }

    return totalToPay - totalPaid;
};


const getClients = getEntitiesDataItems<ClientEntity>('clients');

const normalizeInvoice = (invoice: Invoice | InvoiceReduced): Invoice => ({
    ...invoice,
    relatedDocuments: 'relatedDocuments' in invoice ? invoice.relatedDocuments : [],
    items: 'items' in invoice ? invoice.items : [],
});

const ClientPackForm : FC<ClientPackFormStateProps&ClientPackFormDispatchProps> = ({
    clientPack,
    packSettings,
    type,
    hasChanged = false,
    loading,
    changeLog,
    appointments = {},
    readOnly = false,
    canSeePrices,
    canSeeInvoices,
    canCreateInvoices,
    isAdministrator,
    onLoad,
    onLoadAppointment,
    onOpenAppointment,
    onChange,
    onSubmit,
    onClose,
    onDelete,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const settings = useGeneralSettings();

    const { templates } = usePackTemplates();
    const taxes = useTaxData();
    const allClients = useSelector(getClients);
    const clientSearchConfig = useMemo(() => createClientSearchConfig(allClients), [allClients]);
    const sortedTemplates = useMemo(() => [...templates].sort((a, b) => a.name.localeCompare(b.name)), [templates]);

    const handleClientChanged = React.useCallback((option: string) => {
        const client = clientSearchConfig.getClientByOption(option);
        if (client && onChange) {
            onChange({ ...clientPack, clientId: client.id ?? '', clientName: client.name });
        }
    }, [clientSearchConfig, clientPack, onChange]);

    const handleTemplateSelected = React.useCallback((name: string) => {
        const template = sortedTemplates.find(t => t.name === name);
        if (!template || !onChange) return;
        onChange({
            ...clientPack,
            packTemplateId: template.templateId ?? template.id,
            packName: template.name,
            packVersion: template.version,
            appointmentTypes: template.appointmentTypes,
            price: template.price,
            vat: (taxes.taxRatesMap[template.invoiceItemDetails?.taxId ?? '']?.rate ?? 0) / 100,
            totalMinutesStillAvailable: template.totalMinutes ?? 0,
            totalCreditsStillAvailable: template.totalCredits ?? 0,
            validityFrom: getNowDateString(),
            validityTo: template.validityDays && template.validityDays >= 30
                ? moment().add(template.validityDays / 30, 'months').add(-1, 'day').toISOString()
                : template.validityDays ? moment().add(template.validityDays - 1, 'days').toISOString()
                : undefined,
        });
    }, [sortedTemplates, taxes, clientPack, onChange]);

    React.useEffect(() => {
        onLoad && onLoad();
        onLoadAppointment && clientPack.appointments
            ?.filter(appointmentId => !appointments[appointmentId])
            .forEach(appointmentId => onLoadAppointment(appointmentId));
    }, [])

    const [modal, setModal] = React.useState(false);
    const [invoiceModal, setInvoiceModal] = React.useState(false);
    const [emitReceiptForInvoiceId, setEmitReceiptForInvoiceId] = React.useState<string | undefined>(undefined);

    // Get available expenses for all appointment types in the pack
    const expensesAvailable = useExpensesAvailable(packSettings?.appointmentTypes ?? []);

    const toggleDeleteModal = () => setModal(!modal);

    const hideValidity = readOnly && !clientPack.validityTo

    const latestPackRef = React.useRef(clientPack);
    React.useEffect(() => { latestPackRef.current = clientPack; }, [clientPack]);

    const syncPackFinancialSnapshot = React.useCallback(() => {
        const current = latestPackRef.current;
        if (!current.id || !current.clientId) return;
        getClientPackAsync(current.clientId, current.id)
            .then(fresh => {
                const latest = latestPackRef.current;
                dispatch(updateChangingEntity('clientPacks', {
                    ...latest,
                    invoices: fresh.invoices ?? latest.invoices,
                    payments: fresh.payments ?? latest.payments,
                } as Entity))
            })
            .catch(console.error)
    }, [dispatch])

    const onInvoiceCreated = canCreateInvoices ? React.useCallback((invoice: InvoiceReduced) => {
        const invoices = (clientPack.invoices ?? []).map(normalizeInvoice)
        dispatch(updateChangingEntity('clientPacks', {...clientPack, invoices: [...invoices, normalizeInvoice(invoice)]} as Entity))
        syncPackFinancialSnapshot()
    }, [clientPack, dispatch, syncPackFinancialSnapshot]) : undefined

    const onReceiptEmitted = syncPackFinancialSnapshot

    const totalPaid = getTotalPayments(clientPack.payments);

    const extrasOptions = expensesAvailable.map(e => ({
        value: e,
        label: canSeePrices ? `${e.name} (${formatEuros(e.value)})` : e.name
    }));

    const totalPriceWithoutVAT = React.useMemo(() =>
        (clientPack.price ?? 0) + (clientPack.extras ?? []).reduce((t, e) => t + ((e?.value ?? 0) * (e?.quantity ?? 1)), 0),
        [clientPack.price, clientPack.extras]
    );

    const totalPriceWithVAT = React.useMemo(() =>
        totalPriceWithoutVAT * (1 + clientPack.vat),
        [totalPriceWithoutVAT, clientPack.vat]
    );

    const missingToPay = packSettings?.subscriptionUnit !== undefined
        ? calculateMissingToPayForSubscription(totalPriceWithVAT, packSettings, clientPack)
        : totalPriceWithVAT - totalPaid

    const totalInvoiced = React.useMemo(() =>
        (clientPack.invoices ?? [])
            .filter(i => i.state !== InvoiceState.Canceled)
            .reduce((sum, i) => {
                if (i.type === InvoiceDocumentType.CreditNote) return sum - (i.total ?? 0);
                if (i.type === InvoiceDocumentType.Receipt) return sum;
                return sum + (i.total ?? 0);
            }, 0),
        [clientPack.invoices]
    );

    const invoicePendingReceipt = React.useMemo(() =>
        (clientPack.invoices ?? []).find(i =>
            i.type === InvoiceDocumentType.Invoice
            && i.state !== InvoiceState.Canceled
            && i.state !== InvoiceState.Draft
            && !('hasReceipt' in i && i.hasReceipt)
            && !('relatedDocuments' in i && (i as Invoice).relatedDocuments?.some(d => d.type === InvoiceDocumentType.Receipt))
        ),
        [clientPack.invoices]
    );

    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            {type === 'create' && (
                <>
                    <AutocompleteTextField
                        id="client-pack-client-selector"
                        label={t(TK.client)}
                        value={clientSearchConfig.getOptionByClientId(clientPack.clientId) ?? clientPack.clientName ?? ''}
                        options={clientSearchConfig.options}
                        optionLabelResolver={clientSearchConfig.optionLabelResolver}
                        searchTextResolver={clientSearchConfig.searchTextResolver}
                        searchMatcher={clientSearchConfig.searchMatcher}
                        onValueChanged={handleClientChanged}
                    />
                    <AutocompleteTextField
                        id="client-pack-template-selector"
                        label={`${t(TK.pack)}/${t(TK.subscription)}`}
                        value={clientPack.packName ?? ''}
                        options={sortedTemplates.map(t => t.name)}
                        onValueChanged={handleTemplateSelected}
                        disableClearable
                    />
                </>
            )}
            {clientPack.id &&
                <TextInputField
                    id="client-pack-id"
                    label="Id"
                    value={clientPack.id}
                    disabled={true}
                />
            }
            <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                <TextInputField
                    id="client-pack-pack-name"
                    label={`${t(TK.pack)}/${t(TK.subscription)}`}
                    value={`${clientPack.packName} (v${clientPack.packVersion})`}
                    disabled={true}
                    style={{flexGrow: 1}}
                />
                <DateField
                    id="client-pack-creation-date"
                    label={t(TK.creationDate)}
                    value={(clientPack.creationDate ?? new Date().toISOString()).substring(0, 10)}
                    type="date"
                    disabled={true}
                    style={{flexGrow: 1}}
                />
            </div>
            {packSettings &&
                <AutocompleteTextField
                    id="client-pack-appointment-types"
                    multiSelect
                    options={packSettings.appointmentTypes.map(s => serviceName(s, settings))}
                    values={packSettings.appointmentTypes.map(s => serviceName(s, settings))}
                    disabled={true}
                />
            }
            <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                <div style={{flexGrow: 1, ...hide(!canSeePrices)}}>
                    <NumberInputField
                        id="client-pack-price"
                        label={packSettings?.subscriptionUnit !== undefined
                            ? `${t(TK.price)} (${subscriptionPriceDenomitator(t, packSettings)})`
                            : t(TK.price)
                        }
                        prepend="€"
                        value={clientPack.price}
                        disabled={!onChange || readOnly}
                        onChange={price => onChange!({...clientPack, price: Number(price)})}
                    />
                </div>
                {!!clientPack.totalMinutes ? <>
                    <TextInputField
                        id="client-pack-duration"
                        label={t(TK.duration)}
                        value={minutesToTime(clientPack.totalMinutes)}
                        disabled={true}
                        style={{flexGrow: 1}}
                    />
                    <TextInputField
                        id="client-pack-duration-available"
                        label={t(TK.available)}
                        value={minutesToTime(clientPack.totalMinutesStillAvailable ?? 0)}
                        disabled={true}
                        style={{flexGrow: 1}}
                    />
                </> : !!clientPack.totalCredits ? <>
                    <TextInputField
                        id="client-pack-total-credits"
                        label={t(TK.totalCredits)}
                        value={clientPack.totalCredits}
                        type='number'
                        disabled={true}
                        style={{flexGrow: 1}}
                    />
                    <TextInputField
                        id="client-pack-credits-available"
                        label={t(TK.available)}
                        value={clientPack.totalCreditsStillAvailable}
                        type='number'
                        disabled={true}
                        style={{flexGrow: 1}}
                    />
                </> : null}
            </div>


            {!hideValidity &&
                <div style={{display: 'flex', gap: 'var(--spacer-S)', alignItems: 'end'}}>
                    <DateField
                        id="client-pack-validity-from"
                        label={t(TK.validFrom)}
                        type="date"
                        value={(clientPack.validityFrom ?? clientPack.validityTo)?.substring(0, 10)}
                        disabled={readOnly || !isAdministrator}
                        onChange={validityFrom => onChange!({
                            ...clientPack,
                            validityFrom,
                            validityTo: packSettings?.validityDays && packSettings.validityDays >= 30
                                ? moment(validityFrom).add(packSettings.validityDays / 30, 'months').add(-1, 'day').toISOString()
                                : packSettings?.validityDays ? moment(validityFrom).add(packSettings.validityDays - 1, 'days').toISOString()
                                : undefined,
                        })}
                        style={{flexGrow: 1}}
                    />
                    <DateField
                        id="client-pack-validity-to"
                        label={t(TK.validUpTo)}
                        type="date"
                        value={clientPack.validityTo?.substring(0, 10)}
                        disabled={readOnly || !isAdministrator}
                        onChange={validityTo => onChange!({...clientPack, validityTo})}
                        style={{flexGrow: 1}}
                    />
                    <div style={{flexGrow: 1, paddingBottom: 'var(--spacer-XS)'}}>
                        <span style={{whiteSpace: 'nowrap'}}>{moment(clientPack.validityTo).diff(moment(clientPack.validityFrom), 'days')} {t(TK.days).toLocaleLowerCase()}</span>
                    </div>
                </div>
            }

            <hr style={{width: '100%'}}/>

            <ExtrasList
                id="client-pack"
                expenses={clientPack.extras ?? []}
                expensesOptions={extrasOptions}
                readOnly={readOnly}
                canSeePrices={canSeePrices}
                onAdd={(expense) => onChange!({...clientPack, extras: [...(clientPack.extras ?? []), expense]})}
                onDelete={(index) => onChange!({...clientPack, extras: clientPack.extras?.filter((_, i) => i !== index)})}
                onUpdate={(index, extra) => onChange!({...clientPack, extras: clientPack.extras?.map((e, i) => i === index ? extra : e)})}
            />

            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                    {totalPriceWithVAT === totalPriceWithoutVAT ? (
                        <Typography id="appointment-total-price-label" size='x-large'><b>{formatEuros(totalPriceWithoutVAT)}</b></Typography>
                    ) : (
                        <>
                            <Typography id="appointment-total-price-label" size='x-large'><b>{formatEuros(totalPriceWithVAT)}</b></Typography>
                            <Typography id="appointment-total-price-without-vat-label" size='small' style={{ color: 'var(--text-muted)', marginTop: 'var(--spacer-XXS)' }}>
                                {t(TK.withoutVAT)}: {formatEuros(totalPriceWithoutVAT)}
                            </Typography>
                        </>
                    )}
                    {missingToPay > 0 && (
                        <span style={{color: 'var(--danger-color)', fontSize: '0.875rem'}}>{t(TK.missingPayment)}</span>
                    )}
                </div>
            </div>

            {canSeePrices && !!clientPack.payments?.length && (
                <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t(TK.payment)}</label>
                    {(clientPack.payments ?? []).length > 0 && (
                        <PaymentsList
                            values={clientPack.payments ?? []}
                            canDelete={true}
                            onDelete={(_, idx) => onChange!({...clientPack, payments: clientPack.payments?.filter((_, i) => i !== idx)})}
                            missingToPay={missingToPay}
                        />
                    )}
                </div>
            )}

            {canSeeInvoices && ((clientPack.invoices ?? []).length > 0 || invoiceModal) &&
                <div>
                    <label style={{ fontSize: '0.875rem', display: 'block', fontWeight: 500, marginBottom: 'var(--spacer-XS)' }}>{t(TK.invoices)}</label>
                    <InvoicesTable
                        clientId={clientPack.clientId}
                        clientPack={clientPack}
                        invoices={clientPack.invoices ?? []}
                        createNew={invoiceModal}
                        emitReceiptForInvoiceId={emitReceiptForInvoiceId}
                        hideCreateButton={true}
                        onCreated={(invoice) => { onInvoiceCreated?.(invoice); setInvoiceModal(false); }}
                        onUpdate={invoice => onChange && onChange({
                            ...clientPack,
                            invoices: (clientPack.invoices ?? []).map(existing =>
                                existing.id === invoice.id
                                    ? normalizeInvoice({...existing, ...invoice})
                                    : normalizeInvoice(existing)
                            )
                        })}
                        onReceiptEmitted={onReceiptEmitted}
                        showNavigationButtons={true}
                        onModalClose={() => { setInvoiceModal(false); setEmitReceiptForInvoiceId(undefined); }}
                    />
                </div>
            }

            <div style={{display: 'flex', flexDirection: 'row', gap: 'var(--spacer-XS)', flexWrap: 'wrap'}}>
                {canSeePrices && (
                    <PaymentsButtons
                        entity={clientPack}
                        totalToPay={totalPaid + missingToPay}
                        totalPaid={totalPaid}
                        showManual
                        showAutomatic
                        showMissingValue
                        onPaymentAdded={payment => onChange!({...clientPack, payments: [...clientPack.payments??[], payment]})}
                        onPaymentsChanged={payments => onChange!({...clientPack, payments})}
                    />
                )}
                {canCreateInvoices && invoicePendingReceipt && (
                    <AppButton size="lg" color="primary" style={{width: '14rem'}} outline onClick={() => setEmitReceiptForInvoiceId(invoicePendingReceipt.id)}>
                        <ReceiptIcon fontSize="small"/>
                        {t(TK.emitReceipt)}
                    </AppButton>
                )}
                {canCreateInvoices && !invoicePendingReceipt && Math.abs(totalInvoiced - totalPriceWithVAT) > 0.01 && (
                    <AppButton size="lg" color="primary" style={{width: '14rem'}} outline onClick={() => setInvoiceModal(true)}>
                        <ReceiptIcon fontSize="small"/>
                        {t(TK.issueInvoice)}
                    </AppButton>
                )}
            </div>


            {type === 'edit' && !!clientPack.appointments?.length && <>
                <hr style={{width: '100%'}}/>
                <div>
                    <label style={{ fontSize: '0.875rem', display: 'block', fontWeight: 500, marginBottom: 'var(--spacer-XS)' }}>{t(TK.appointments)}</label>
                    {!clientPack.appointments
                        ?   <Typography><i>{t(TK.notInUseYet)}</i></Typography>
                        :   <ListGroup id="client-pack-appointments-list">
                                {clientPack.appointments.map(id => appointments[id]).map((appointment, i) => (
                                    <ListGroupItem key={appointment?.id ?? i}>
                                        {!appointment
                                            ?   <Typography><i>{t(TK.loading)}...</i></Typography>
                                            :   <AppButton
                                                    id={`client-pack-appointment-button-${appointment.id}`}
                                                    style={{padding: 0}}
                                                    color="link"
                                                    onClick={() => onOpenAppointment && onOpenAppointment(appointment.id)}
                                                >
                                                    {getAppointmentDesc(clientPack.clientName, appointment, settings)}
                                                </AppButton>
                                        }
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                    }
                </div>
            </>}

            <div className="buttonsContainer" >
                { onClose && (<AppButton id="client-pack-cancel-button" onClick={onClose}>{t(TK.cancel)}</AppButton>) }
                { onDelete && !readOnly && !clientPack.appointments?.length &&
                    <AppButton
                        id="client-pack-delete-button"
                        outline
                        color="danger"
                        onClick={toggleDeleteModal}
                    >
                        <Delete />
                    </AppButton> }
                { onSubmit && !readOnly &&
                    <AppButton
                        id="client-pack-save-button"
                        color="success"
                        disabled={loading || !(hasChanged || type === 'create')}
                        onClick={() => onSubmit(clientPack)}
                    >
                        {loading && <Spinner size="sm" color="secundary"/>}
                        {t(type === 'edit' ? TK.save : TK.create)}
                    </AppButton>
                }

                { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
            </div>

            { onDelete && !readOnly &&
                <Modal isOpen={modal} toggle={toggleDeleteModal}>
                    <ModalHeader toggle={toggleDeleteModal}>{t(TK.areYouSure)}</ModalHeader>
                    <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                    <ModalFooter>
                        <AppButton color="danger" onClick={() => { onDelete(); toggleDeleteModal(); }}>{t(TK.delete)}</AppButton>{' '}
                        <AppButton color="secondary" onClick={toggleDeleteModal}>{t(TK.cancel)}</AppButton>
                    </ModalFooter>
                </Modal>
            }
        </Form>
    );
}

export default ClientPackForm;
